import { locationRepair } from './lead/locationRepair';
import type { Heat } from '../../../shared/enums';
import { HEATS } from '../../../shared/enums';
import type { Objection, Visit } from '../../../shared/types';
import { shell } from '../app';
import { getWatchlistData, requestFeatures } from '../data/store';
import type { WatchlistData } from '../data/store';
import { getLeadDetail, onStoreChange, updateLead } from '../data/store';
import { diffDays, formatDate, formatDateTime, formatTime, relativeDay, todayLocal } from '../logic/dates';
import { btn, empty, heatDot, linkBtn, section, segmented, stageSelect } from '../ui/components';
import { featurePicker } from '../ui/featurePicker';
import type { FeaturePicker } from '../ui/featurePicker';
import { h, replaceChildren, span } from '../ui/dom';
import type { Child } from '../ui/dom';
import {
  BUSINESS_TYPE_LABELS,
  HEAT_LABELS,
  LANGUAGE_NAMES,
  OBJECTION_LABELS,
  OUTCOME_LABELS,
  OWNER_ROLE_LABELS,
  POS_LABELS,
  SPOKE_WITH_LABELS,
  money,
} from '../ui/labels';
import type { View } from '../ui/router';
import { dealCard } from './lead/dealCard';
import { provenanceSection } from './lead/provenance';
import { leadDraftsPanel } from './leadDrafts';

const contactLink = (label: string, href: string, cls = '') =>
  h(
    'a',
    {
      class: `contact ${cls}`.trim(),
      href,
      target: href.startsWith('http') ? '_blank' : undefined,
      rel: 'noopener',
    },
    label,
  );

const kv = (k: string, v: Child) => h('div', { class: 'kv' }, span('kv__k', k), span('kv__v', v));

const objectionChip = (o: Objection) =>
  span('objchip', OBJECTION_LABELS[o.type], o.detail ? span('objchip__detail', ` — ${o.detail}`) : null);

const visitCard = (v: Visit, objections: Objection[], today: string) =>
  h(
    'article',
    { class: 'visit' },
    h(
      'header',
      { class: 'visit__head' },
      span('visit__when mono', formatDateTime(v.visited_at, today)),
      span(`visit__outcome visit__outcome--${v.outcome}`, OUTCOME_LABELS[v.outcome]),
    ),
    h('div', { class: 'visit__who' }, `Spoke with ${SPOKE_WITH_LABELS[v.spoke_with].toLowerCase()}`),
    v.notes ? h('p', { class: 'visit__notes' }, v.notes) : null,
    v.next_action || v.next_action_date
      ? h(
          'div',
          { class: 'visit__next' },
          span('visit__next-label mono', 'NEXT'),
          ' ',
          v.next_action ?? 'Follow up',
          v.next_action_date
            ? span(
                'mono muted',
                ` · ${relativeDay(v.next_action_date, today)}${v.next_action_time ? ` · ${formatTime(v.next_action_time)}` : ''}`,
              )
            : null,
        )
      : null,
    objections.length ? h('div', { class: 'visit__objections' }, objections.map(objectionChip)) : null,
  );

/** The "+ Request feature" picker lives in the view, not in one render: a background sync
 *  re-renders the page and must not drop picks made so far. */
interface RequestUi {
  picker: FeaturePicker | null;
  open: boolean;
}

const featureSection = (leadId: string, data: WatchlistData, ui: RequestUi): HTMLElement => {
  const mine = data.requests.filter((r) => r.lead_id === leadId);
  const featureById = new Map(data.features.map((f) => [f.id, f]));
  const pickerWrap = h('div', { class: 'card', hidden: !ui.open }, ui.picker?.el);
  const toggle = btn('+ Request feature', {
    variant: 'subtle',
    size: 'sm',
    onClick: () => {
      ui.picker ??= featurePicker(data.features);
      ui.open = !ui.open;
      replaceChildren(pickerWrap, ui.picker.el);
      pickerWrap.hidden = !ui.open;
      saveBtn.hidden = !ui.open;
    },
  });
  const saveBtn = btn('Save feature request', {
    variant: 'primary',
    size: 'sm',
    class: 'mt-2',
    onClick: async () => {
      const picker = ui.picker;
      const asks = picker?.getAsks() ?? [];
      if (asks.length === 0) {
        shell.toast('Pick or type a feature first', { kind: 'error' });
        return;
      }
      // The save re-renders the page: it should come back with the picker closed and empty.
      ui.picker = null;
      ui.open = false;
      try {
        await requestFeatures(leadId, asks);
        shell.toast(`${asks.length} feature request${asks.length === 1 ? '' : 's'} saved`, {
          kind: 'success',
        });
      } catch (err) {
        ui.picker = picker;
        ui.open = true;
        shell.toast(`Could not save: ${err instanceof Error ? err.message : String(err)}`, { kind: 'error' });
      }
    },
  });
  saveBtn.hidden = !ui.open;
  return section(
    {
      title: 'Feature requests',
      count: mine.length,
      action: linkBtn('Watchlist', '#/watchlist', { variant: 'subtle', size: 'sm' }),
    },
    mine.length
      ? h(
          'div',
          { class: 'card' },
          mine.map((r) =>
            h(
              'div',
              { class: 'kv' },
              span('kv__k', featureById.get(r.feature_id)?.name ?? '(removed)'),
              span('kv__v muted small', r.notes ?? ''),
            ),
          ),
        )
      : null,
    toggle,
    pickerWrap,
    saveBtn,
  );
};

export const leadView: View = ({ root, params }) => {
  shell.setChrome(true);
  const id = params.id ?? '';
  const host = h('div', { class: 'page page--lead' });
  root.appendChild(host);
  const requestUi: RequestUi = { picker: null, open: false };
  const drafts = leadDraftsPanel(id);
  let renderSeq = 0;

  // a mirror write that fails leaves the control on the new value: say so and paint from the mirror
  const saveFailed = (err: unknown) => {
    shell.toast(`Could not save: ${err instanceof Error ? err.message : String(err)}`, { kind: 'error' });
    void render();
  };

  // Everything here comes from the mirror (D4); the online-only drafts panel fills itself in.
  const render = async () => {
    const mine = ++renderSeq;
    const today = todayLocal();
    const [detail, watchlist] = await Promise.all([getLeadDetail(id), getWatchlistData()]);
    if (mine !== renderSeq) return; // a newer render owns the page
    if (!detail) {
      shell.setTitle('Lead', { back: '#/pipeline' });
      replaceChildren(
        host,
        empty('This lead is not on this device.', 'It may have been deleted, or not synced yet.'),
      );
      return;
    }
    const { lead, visits, objections, signals } = detail;
    shell.setTitle('Lead', {
      back: '#/pipeline',
      log: `#/log?lead=${encodeURIComponent(lead.id)}`,
      action: linkBtn('Edit', `#/leads/${encodeURIComponent(lead.id)}/edit`, {
        variant: 'subtle',
        size: 'sm',
      }),
    });

    const contacts: HTMLElement[] = [];
    if (lead.phone)
      contacts.push(contactLink('Call', `tel:${lead.phone}`), contactLink('Text', `sms:${lead.phone}`));
    if (lead.email) contacts.push(contactLink('Email', `mailto:${lead.email}`));
    if (lead.instagram)
      contacts.push(contactLink('Instagram', `https://instagram.com/${lead.instagram.replace(/^@/, '')}`));

    const contractBits: Child[] = [];
    if (lead.contract_end_date) {
      const left = diffDays(lead.contract_end_date, today);
      contractBits.push(
        kv('Contract ends', [
          formatDate(lead.contract_end_date, today),
          ' ',
          span(
            `daysleft mono ${left < 0 ? 'daysleft--expired' : left <= 30 ? 'daysleft--30' : left <= 60 ? 'daysleft--60' : 'daysleft--90'}`,
            left < 0 ? `${-left}d ago` : left === 0 ? 'today' : `${left}d`,
          ),
        ]),
      );
    }
    if (lead.etf_amount !== null) contractBits.push(kv('ETF', money(lead.etf_amount)));

    const byVisit = new Map<string, Objection[]>();
    const orphan: Objection[] = [];
    for (const o of objections) {
      if (o.visit_id) byVisit.set(o.visit_id, [...(byVisit.get(o.visit_id) ?? []), o]);
      else orphan.push(o);
    }

    // two columns on the desktop (D65): who and what on the side, the history in the main column;
    // on the phone both wrappers are transparent and the page reads as one column, as before
    replaceChildren(
      host,
      h(
        'div',
        { class: 'lead__side' },
        h(
          'div',
          { class: 'hero' },
          h('h2', { class: 'hero__name' }, heatDot(lead.heat), ' ', lead.business_name),
          h(
            'div',
            { class: 'hero__meta' },
            BUSINESS_TYPE_LABELS[lead.business_type],
            lead.neighborhood ? ` · ${lead.neighborhood}` : '',
          ),
          lead.address ? h('div', { class: 'hero__address' }, lead.address) : null,
          h(
            'div',
            { class: 'hero__controls' },
            stageSelect(
              lead.stage,
              (stage) =>
                void updateLead(lead.id, { stage })
                  .then(() => shell.toast(`Stage → ${stage.replace('_', ' ')}`))
                  .catch(saveFailed),
            ),
            segmented<Heat>({
              options: HEATS.map((x) => ({ value: x, label: HEAT_LABELS[x] })),
              value: lead.heat,
              onChange: (heat) => void updateLead(lead.id, { heat }).catch(saveFailed),
              columns: 3,
              size: 'md',
            }),
          ),
        ),
        locationRepair(lead),
        section(
          { title: 'Contact' },
          h(
            'div',
            { class: 'card' },
            lead.owner_name || lead.owner_role
              ? kv('Owner', [
                  lead.owner_name ?? '—',
                  lead.owner_role ? span('muted', ` · ${OWNER_ROLE_LABELS[lead.owner_role]}`) : null,
                ])
              : null,
            kv('Speaks', LANGUAGE_NAMES[lead.owner_language]),
            lead.best_visit_window ? kv('Best time', lead.best_visit_window) : null,
            contacts.length
              ? h('div', { class: 'contacts' }, contacts)
              : h('p', { class: 'muted small' }, 'No phone or email yet — tap Edit to add.'),
          ),
        ),
        section(
          { title: 'Current POS' },
          h(
            'div',
            { class: 'card' },
            kv('System', [
              POS_LABELS[lead.current_pos],
              lead.pos_monthly_cost ? span('muted', ` · ${money(lead.pos_monthly_cost)}/mo`) : null,
            ]),
            ...contractBits,
            lead.place_id ? kv('Place ID', span('mono small', lead.place_id)) : null,
          ),
        ),
        dealCard(lead, saveFailed),
        lead.notes ? section({ title: 'Notes' }, h('p', { class: 'card card--text' }, lead.notes)) : null,
      ),
      h(
        'div',
        { class: 'lead__main' },
        section(
          { title: 'Visits', count: visits.length },
          visits.length
            ? visits.map((v) => visitCard(v, byVisit.get(v.id) ?? [], today))
            : empty('No visits logged yet.'),
        ),
        orphan.length
          ? section({ title: 'Other objections' }, h('div', { class: 'card' }, orphan.map(objectionChip)))
          : null,
        featureSection(lead.id, watchlist, requestUi),
        provenanceSection(signals, today),
        drafts.section(lead),
      ),
      h(
        'div',
        { class: 'actionbar' },
        linkBtn('+ Log visit', `#/log?lead=${encodeURIComponent(lead.id)}`, {
          variant: 'primary',
          size: 'lg',
          class: 'w-full',
        }),
      ),
    );
  };

  void render();
  void drafts.load();
  return onStoreChange(() => {
    void render();
    void drafts.load(); // background refresh: a page opened offline fills in once a sync succeeds
  });
};
